import { isReferenceOf } from './services';
import { getExtensionValue, getIdentifier, resolveId } from '@medplum/core';
import {
  APPOINTMENT_PARTICIPANT_TYPE_ADMITTER_CODING,
  AMBULATORY_ENCOUNTER_CLASS_CODING,
  RESCHEDULED_CODING,
  VIRTUAL_ENCOUNTER_CLASS_CODING,
} from './codings';
import {
  APPOINTMENT_CUSTOM_INFO_EXTENSION,
  APPOINTMENT_SERIES_METADATA_EXTENSION,
  DATA_FROM_HEALTHIE_EXTENSION,
  SCHEDULING_PARAMETERS_EXTENSION,
} from './extensions';
import { APPOINTMENT_SERIES_IDENTIFIER_SYSTEM, type ZoomRecurrenceType } from './constants';
import {
  ActivityDefinition,
  Appointment,
  Bundle,
  BundleEntry,
  Coding,
  Location,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
  Resource,
  Slot,
  UsageContext,
} from '@medplum/fhirtypes';
import {
  APPOINTMENT_TYPES_SYSTEM,
  BARIENDO_CT_SYSTEM,
  OVERFLOW_PRACTITIONER_ROLE_CONTEXT_CODE,
  ProviderRole,
} from './constants';

export type Guest = Patient | Practitioner;
export type VirtualServiceType = 'video' | 'phone';

export interface AppointmentCustomInfo {
  virtualServiceType?: VirtualServiceType;
  virtualServiceUrl?: string;
  virtualServicePhone?: string;
  /**
   * Internal/admin URL for virtual services (e.g., Zoom startUrl).
   * Intended for providers and may bypass waiting rooms or passwords.
   */
  virtualServiceAdminUrl?: string;
}

export interface BookAppointmentRequest {
  activityDefinition: Reference<ActivityDefinition>;
  practitioner?: Reference<Practitioner> | null;
  partner?: Reference<Organization> | null;
  guests: Reference<Guest>[];
  date: string;
  startTime: string;
  timezone?: string;
  visitType: Coding;
  location?: Reference<Location>;
  /** When updating a recurring series, controls which occurrences are updated. */
  editScope?: 'single' | 'thisAndFollowing' | 'all';
  /**
   * Optional list of appointment types (codings) this booking/block should apply to.
   * Primarily used for "block" appointments to indicate which service types are blocked/unblocked.
   *
   * Stored on the Slot as `serviceType` CodeableConcepts.
   */
  appointmentTypes?: Coding[];
  notes?: string;
  richTextNotes?: string;
  customInfo?: AppointmentCustomInfo;
  /** When provided, the bot updates this Appointment instead of creating a new one. */
  appointment?: Reference<Appointment>;
  /** Shared UUID for materialized recurring series; stored on Appointment + Slot identifiers. */
  seriesId?: string;
  recurrence?: RecurrenceSettings;
  /** 1-based index within the series; set per occurrence when materializing. */
  seriesIndex?: number;
  /** Total planned occurrences when known at creation. */
  seriesTotal?: number;
}

export interface SetWeeklyAvailabilityRequest {
  practitioner: Reference<Practitioner>;
  encounterClass: Coding;
  timezone?: string;
  location?: Reference<Location>;
  days: {
    dayOfWeek: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
    enabled: boolean;
    timeRanges: {
      startTime: string; // e.g., "09:00:00"
      endTime: string; // e.g., "17:00:00"
      appointmentTypes: Coding[]; // Coding objects from appointment-types.json
    }[];
  }[];
}

export interface SetOneTimeAvailabilityRequest {
  practitioner: Reference<Practitioner>;
  encounterClass: Coding;
  timezone: string;
  location?: Reference<Location>;
  date: string;
  startTime: string;
  endTime: string;
  appointmentTypes: Coding[];
  /** Optional scheduler-facing guideline note, stored as a sub-extension on the Slot. */
  schedulingGuideline?: string;
  /** When provided, the bot updates this Slot instead of creating a new one. */
  slot?: Reference<Slot>;
}

export interface BookingWarning {
  participantReference: string;
  participantName: string;
  message: string;
}

export interface BookAppointmentResult {
  appointment: Appointment;
  slot: Slot;
  warnings?: BookingWarning[];
}

/** Per-message breakdown for aggregated series warnings. */
export interface BookAppointmentWarningDetail {
  message: string;
  occurrenceIndexes: number[];
}

/**
 * Result when creating multiple Appointments (recurring series).
 */
export interface BookAppointmentSeriesResult {
  results: BookAppointmentResult[];
  warnings?: BookingWarning[];
  warningDetails?: BookAppointmentWarningDetail[];
}

/**
 * Shared recurrence settings (used by recurring calendar blocks and appointments).
 */
export interface RecurrenceSettings {
  frequency: 'daily' | 'weekly' | 'monthly';
  /** Every N units of the selected frequency. Defaults to 1. */
  interval?: number;
  /** Number of occurrences to create (includes the first). Typically 1-20 in UI. */
  count?: number;
  /** Inclusive end date in YYYY-MM-DD (local to the appointment timezone). */
  untilDate?: string;
  /**
   * When frequency is `monthly`: Nth weekday of each month (e.g. 3rd Friday).
   * `weekday`: 0 = Sunday .. 6 = Saturday (JavaScript / dayjs convention).
   * If a month has fewer than N of that weekday, that month is skipped.
   */
  monthlyNthWeekday?: {
    n: 1 | 2 | 3 | 4 | 5;
    weekday: number;
  };
}

/**
 * Request to create or update a **calendar block** as a single FHIR `Slot` only.
 *
 * This flow does **not** create or update an `Appointment`. The block is stored as
 * `Slot.status = busy-unavailable` on the provider's appointment-availability `Schedule`.
 */
export interface BookCalendarBlockRequest {
  practitioner: Reference<Practitioner>;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  location?: Reference<Location>;
  /**
   * When set, stored on the Slot as `serviceType` to indicate which offer types are blocked.
   * When empty, the Slot still marks time as unavailable via status + optional marker coding.
   */
  appointmentTypes?: Coding[];
  notes?: string;
  richTextNotes?: string;
  seriesId?: string;
  recurrence?: RecurrenceSettings;
  detachFromSeries?: boolean;
  /** When provided, the bot updates this Slot instead of creating a new one. */
  slot?: Reference<Slot>;
}

export interface BookCalendarBlockResult {
  slot: Slot;
  warnings?: BookingWarning[];
}

/**
 * Result when creating multiple block Slots (recurring series).
 */
export interface BookCalendarBlockSeriesResult {
  results: BookCalendarBlockResult[];
}

/** When true, `book-appointment` materializes N occurrences (not an update). */
export function shouldCreateAppointmentSeries(input: BookAppointmentRequest, isUpdate: boolean): boolean {
  if (isUpdate) {
    return false;
  }
  if (!input.recurrence) {
    return false;
  }
  if (!input.seriesId) {
    return false;
  }
  return true;
}

export interface FindAvailabilityRequest {
  serviceTypes?: string[];
  practitionersRoles?: Reference<PractitionerRole>[];
  roles?: ProviderRole[];
  locations?: string[];
  startTime: string;
  endTime: string;
}

/** Availability window, optionally scoped to a practitioner (resourceId) for day-view columns */
export interface FindAvailabilityWindow {
  startTime: string;
  endTime: string;
  /** Practitioner reference (e.g. Practitioner/xxx) when per-provider availability is requested */
  resourceId?: string;
}

/** `pac-available-now` bot result: is any Patient Access provider inside an availability block right now? */
export interface PacAvailableNowResponse {
  available: boolean;
}

/**
 * Zoom recurrence payload for `type: 8` meetings.
 * See https://developers.zoom.us/docs/api/rest/reference/zoom-api/methods/#operation/meetingCreate
 */
export interface ZoomRecurrenceRequest {
  /** Zoom: 1 = daily, 2 = weekly, 3 = monthly */
  type: ZoomRecurrenceType;
  repeat_interval: number;
  /** End after N occurrences (inclusive of the first). */
  end_times: number;
  /** Weekly: Zoom weekdays "1"-"7" (1=Sunday … 7=Saturday), comma-separated if multiple. */
  weekly_days?: string;
  /** Monthly (week-of-month): 1–4 or -1 (last). Omit for day-of-month style. */
  monthly_week?: number;
  /** Monthly: 1=Sunday … 7=Saturday */
  monthly_week_day?: number;
}

export interface CreateZoomMeetingRequest {
  duration: number;
  start: string;
  meetingTitle: string;
  timezone?: string;
  /** When set, creates a recurring meeting (Zoom type 8). */
  recurrence?: ZoomRecurrenceRequest;
}

export interface CreateZoomMeetingResponse {
  joinUrl: string;
  startUrl: string;
}

export interface UpdateZoomMeetingRequest {
  meetingId: string;
  duration?: number;
  start?: string;
  meetingTitle?: string;
  timezone?: string;
  occurrenceIndex?: number;
}

export interface GetZoomHostLinkRequest {
  joinUrl: string;
}

export interface GetZoomHostLinkResponse {
  hostUrl: string;
}

/** Whether a `useContext` entry lists overflow roles (`OVERFLOW_PRACTITIONER_ROLE_CONTEXT_CODE`). */
export function isOverflowRoleContext(context: UsageContext): boolean {
  return context.code?.system === BARIENDO_CT_SYSTEM && context.code.code === OVERFLOW_PRACTITIONER_ROLE_CONTEXT_CODE;
}

export interface AllowedRoleOptions {
  /** Count overflow roles too. Only for a caller that names the provider itself. */
  includeOverflow?: boolean;
}

/**
 * Returns the list of allowed provider roles for a given ActivityDefinition.
 * If no allowed roles are configured, this returns an empty array, meaning no restrictions.
 */
export function getAllowedProviderRolesForActivityDefinition(
  activityDefinition: ActivityDefinition,
  { includeOverflow = false }: AllowedRoleOptions = {}
): ProviderRole[] {
  const useContexts = (activityDefinition.useContext ?? []).filter(
    (ctx) => includeOverflow || !isOverflowRoleContext(ctx)
  );

  const codes = useContexts
    .flatMap((ctx) => ctx.valueCodeableConcept?.coding ?? [])
    .map((coding) => coding.code as ProviderRole | undefined)
    .filter((code): code is ProviderRole => !!code && Object.values(ProviderRole).includes(code));

  return Array.from(new Set(codes));
}

/**
 * If the ActivityDefinition has no explicit allowed roles configured, all roles are treated as allowed.
 */
export function isProviderRoleAllowedForActivityDefinition(
  activityDefinition: ActivityDefinition,
  role: ProviderRole | undefined,
  options: AllowedRoleOptions = {}
): boolean {
  if (!role) {
    // If we can't determine the role, don't block scheduling by default.
    return true;
  }

  const allowedRoles = getAllowedProviderRolesForActivityDefinition(activityDefinition, options);
  if (allowedRoles.length === 0) {
    // If no rule is configured it should not be allowed to anybody
    return false;
  }

  return allowedRoles.includes(role);
}

/**
 * Extracts the actors from an Appointment's participant list.
 */
export function extractActorsFromAppointment<T extends Resource | Reference<Resource>>(
  appointment: Appointment,
  resourceType: string
): T[] {
  const actors = appointment.participant
    ?.filter((p) => isReferenceOf(p.actor, resourceType))
    .map((p) => (p.actor?.resource as T | undefined) || (p.actor as T | undefined))
    .filter((p): p is T => p !== undefined);
  return actors ?? [];
}

/**
 * Extracts a custom info item from an Appointment's custom info extension.
 */
export function extractCustomInfoFromAppointment(appointment: Appointment, itemName: string) {
  return getExtensionValue(appointment, APPOINTMENT_CUSTOM_INFO_EXTENSION, itemName);
}

/**
 * Extracts providers (Practitioners) from an Appointment's participant list.
 */
export function extractPractitionersFromAppointment(appointment: Appointment) {
  return extractActorsFromAppointment<Practitioner | Reference<Practitioner>>(appointment, 'Practitioner');
}

/**
 * Gets the host of the appointment (Provider who is responsible for the appointment).
 */
export function getAppointmentHostProvider(appointment: Appointment) {
  return appointment.participant?.find(
    (participant) =>
      isReferenceOf(participant.actor, 'Practitioner') &&
      participant.type?.some((type) =>
        type.coding?.some((coding) => coding.code === APPOINTMENT_PARTICIPANT_TYPE_ADMITTER_CODING.code)
      )
  );
}

/**
 * Extracts the patients from an Appointment's participant list.
 */
export function extractPatientsFromAppointment(appointment: Appointment) {
  return extractActorsFromAppointment<Patient | Reference<Patient>>(appointment, 'Patient');
}

/**
 * Extracts the location from an Appointment's participant list.
 */
export function extractLocationFromAppointment(appointment: Appointment) {
  return extractActorsFromAppointment<Location | Reference<Location>>(appointment, 'Location');
}

/**
 * Extracts the partner from an Appointment's supporting information list.
 */
export function extractPartnerFromAppointment(appointment: Appointment) {
  return appointment.supportingInformation?.find((si) => isReferenceOf(si, 'Organization')) as
    | Reference<Organization>
    | undefined;
}

/**
 * Extracts the virtual service URL from an Appointment's custom info extension.
 */
export function extractVirtualServiceUrlFromAppointment(appointment: Appointment) {
  return extractCustomInfoFromAppointment(appointment, 'virtual-service-url') as string | undefined;
}

/**
 * Extracts the numeric Zoom meeting ID from a meeting join URL (`/j/{id}` path segment).
 *
 * Lives here rather than in the bots' Zoom service because the provider app needs
 * it too — it is the only way to get from an Appointment to the meeting a
 * recording belongs to.
 */
export function extractMeetingIdFromJoinUrl(joinUrl: string): string | undefined {
  return joinUrl.match(/\/j\/(\d+)/)?.[1];
}

/**
 * Extracts the admin virtual service URL (e.g., Zoom startUrl) from an Appointment's custom info extension.
 */
export function extractVirtualServiceAdminUrlFromAppointment(appointment: Appointment) {
  return extractCustomInfoFromAppointment(appointment, 'virtual-service-admin-url') as string | undefined;
}

/**
 * Extracts the virtual service phone number from an Appointment's custom info extension.
 */
export function extractVirtualServicePhoneNumberFromAppointment(appointment: Appointment) {
  return extractCustomInfoFromAppointment(appointment, 'virtual-service-phone') as string | undefined;
}

export interface PatientBookAppointmentInput {
  patientId: string; // "Patient/uuid"
  practitionerId: string; // "Practitioner/uuid"
  serviceType: string; // code from APPOINTMENT_TYPES_SYSTEM
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  timezone?: string; // IANA timezone, defaults to UTC
  encounterClass: 'VR' | 'AMB';
  locationId?: string; // "Location/uuid" — required when AMB
  comment?: string;
}

export interface PatientRescheduleAppointmentInput {
  appointmentId: string; // "Appointment/uuid"
  patientId: string; // "Patient/uuid" — used to verify patient is a participant
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  timezone?: string; // IANA timezone, defaults to UTC
  locationId?: string; // "Location/uuid" — optional, allows changing location for AMB encounters
  comment?: string; // optional updated comment
}

/**
 * Extracts the encounter class (virtual/in-person) from an Appointment's extensions.
 */
export function extractContactTypeFromAppointment(appointment: Appointment) {
  const encounterClass = getExtensionValue(appointment, SCHEDULING_PARAMETERS_EXTENSION, 'encounterClass');
  const virtualServiceType = getExtensionValue(appointment, APPOINTMENT_CUSTOM_INFO_EXTENSION, 'virtual-service-type');
  const originalEncounterData = getExtensionValue(appointment, DATA_FROM_HEALTHIE_EXTENSION) as string;
  const originalEncounterContactType = JSON.parse(originalEncounterData ?? '{}').contact_type?.toLowerCase();

  if (encounterClass && typeof encounterClass === 'object' && 'code' in encounterClass) {
    const code = (encounterClass as { code?: string }).code;
    if (code === VIRTUAL_ENCOUNTER_CLASS_CODING.code) {
      if (virtualServiceType === 'video') {
        return 'Video Call' as const;
      }

      if (virtualServiceType === 'phone') {
        return 'Phone Call' as const;
      }

      return undefined;
    }

    if (originalEncounterContactType === 'phone call') {
      return 'Phone Call' as const;
    }

    if (code === AMBULATORY_ENCOUNTER_CLASS_CODING.code) {
      return 'In-Person' as const;
    }
  }

  return undefined;
}

export type ContactType = ReturnType<typeof extractContactTypeFromAppointment>;

export function getAppointmentSeriesId(appointment: Appointment): string | undefined {
  return getIdentifier(appointment, APPOINTMENT_SERIES_IDENTIFIER_SYSTEM);
}

export function getAppointmentSeriesIndex(appointment: Appointment): number | undefined {
  const v = getExtensionValue(appointment, APPOINTMENT_SERIES_METADATA_EXTENSION, 'seriesIndex');
  return typeof v === 'number' ? v : undefined;
}

export function getAppointmentSeriesTotal(appointment: Appointment): number | undefined {
  const v = getExtensionValue(appointment, APPOINTMENT_SERIES_METADATA_EXTENSION, 'seriesTotal');
  return typeof v === 'number' ? v : undefined;
}

/**
 * True when this appointment was cancelled because it was moved to a new time,
 * rather than genuinely called off.
 *
 * Orbit models a provider-side reschedule as `status: cancelled` plus a
 * RESCHEDULED `cancelationReason`, and books the new time as a separate
 * Appointment — so the marker is the only way to tell the two apart. (A patient
 * self-reschedule updates the existing Appointment in place and never lands
 * here.)
 */
export function isAppointmentRescheduled(appointment: Appointment): boolean {
  const reason = appointment.cancelationReason;
  if (!reason) return false;
  return (
    reason.coding?.some((c) => c.system === RESCHEDULED_CODING.system && c.code === RESCHEDULED_CODING.code) ?? false
  );
}

/**
 * Maps contact type from Appointment to Customer.io modality string.
 */
export function mapContactTypeToModality(contactType: ContactType): 'video' | 'phone' | 'in-person' | undefined {
  if (contactType === 'Video Call') return 'video';
  if (contactType === 'Phone Call') return 'phone';
  if (contactType === 'In-Person') return 'in-person';
  return undefined;
}

export enum AvailabilityTypeEnum {
  WEEKLY = 'weekly',
  ONETIME = 'onetime',
}

export type AvailabilityType = AvailabilityTypeEnum;

export interface CreateAppointmentServiceRequestInput {
  patient: Reference<Patient>;
  /** Procedure code from the appointment-types CodeSystem (e.g. "esg-procedure") */
  procedureCode: string;
  /** Human-readable display name for the procedure (e.g. "ESG Stomach Tightening") */
  procedureDisplay?: string;
}

// ── Reschedule link helpers ──────────────────────────────────────────

/**
 * Mapping from Medplum appointment-type CodeSystem codes to the patient
 * portal's `BariendoAppointmentType` query param values.
 *
 * Only types that support patient self-rescheduling are included.
 * Keep in sync with bariendo-be's APPOINTMENT_TYPE_TO_MEDPLUM_CODE, and with its
 * SELF_RESCHEDULABLE_APPOINTMENT_TYPES: bariendo-be sends the booking confirmation SMS, which
 * promises this link on an N reply, only for those types (BAR-2784). bariendo-be's signed-out
 * booking from this link accepts only these types too (BAR-3048).
 */
const MEDPLUM_CODE_TO_PATIENT_PORTAL_TYPE: Record<string, string> = {
  'app-initial-consultation-visit': 'first_call_pa',
  'pn-consultation-visit': 'pn_consultation',
  'initial-wellness-visit': 'initial_wellness_visit',
  'wellness-follow-up-visit': 'wellness_follow_up_visit',
  'app-follow-up-visit': 'virtual_follow_up',
  'dietitian-follow-up-visit': 'dietitian_follow_up',
  'intro-call': 'phone_call_pn',
};

/**
 * Build a patient-facing reschedule link for an appointment.
 *
 * The appointment id lets a signed-out patient book from the link without logging in: bariendo-be
 * derives the patient, type and provider from that appointment (BAR-3048). A signed-in patient's
 * portal ignores it.
 *
 * Returns `undefined` if the appointment type doesn't support self-rescheduling.
 */
export function getPatientRescheduleLink(patientPortalUrl: string, appointment: Appointment): string | undefined {
  const code = appointment.appointmentType?.coding?.find((c) => c.system === APPOINTMENT_TYPES_SYSTEM)?.code;

  if (!code) return undefined;

  const portalType = MEDPLUM_CODE_TO_PATIENT_PORTAL_TYPE[code];
  if (!portalType) return undefined;

  const practitionerRef = extractPractitionersFromAppointment(appointment)?.[0];
  const providerId = resolveId(practitionerRef);

  let link = `${patientPortalUrl}/schedule-call?type=${portalType}`;
  if (providerId) {
    link += `&provider_id=${providerId}`;
  }
  if (appointment.id) {
    link += `&appointment=${appointment.id}`;
  }
  return link;
}

/**
 * Builds a FHIR Bundle transaction to cancel an appointment and remove its associated slot.
 * Uses the Parameters resource format for PATCH operations as supported by Medplum.
 */
export function buildCancelAppointmentBundle(appointment: Appointment): Bundle {
  const slotId = resolveId(appointment.slot?.[0]);

  const bundleEntries: BundleEntry[] = [
    {
      request: {
        method: 'PATCH',
        url: `Appointment/${appointment.id}`,
      },
      resource: {
        resourceType: 'Parameters',
        parameter: [
          // Test the version ID to prevent race conditions.
          // Reference: https://medplum.com/docs/fhir-datastore/updating-data#patch-operation
          {
            name: 'operation',
            part: [
              { name: 'op', valueCode: 'test' },
              { name: 'path', valueString: '/meta/versionId' },
              { name: 'value', valueString: JSON.stringify(appointment.meta?.versionId) },
            ],
          },
          {
            name: 'operation',
            part: [
              { name: 'op', valueCode: 'replace' },
              { name: 'path', valueString: '/status' },
              { name: 'value', valueString: JSON.stringify('cancelled') },
            ],
          },
        ],
      },
    },
  ];

  if (slotId) {
    bundleEntries.push({
      request: {
        method: 'DELETE',
        url: `Slot/${slotId}`,
      },
    });
  }

  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: bundleEntries,
  };
}
